import KpiCard from '../components/KpiCard';
import PageHeader from '../components/PageHeader';
import { useApi } from '../lib/api';
import { formatCents, formatPercent } from '../lib/format';

type Metrics = {
  experiences: { total: number; last7d: number; last30d: number; published: number; draft: number; archived: number };
  revenue: { last30dCents: number; allTimeCents: number; currency: string };
  conversion: { paidCount: number; paidRate: number };
  uploads: { pending: number; processing: number; failed: number; ready: number };
  avgGuestsPerExperience: number;
};

export default function Dashboard() {
  const { data, error, loading } = useApi<Metrics>('/api/metrics');

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Overview of the surprise platform" />

      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Error: {error.message}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              label="Experiences (total)"
              value={data.experiences.total}
              hint={`${data.experiences.published} published · ${data.experiences.draft} drafts`}
            />
            <KpiCard label="New (7 days)" value={data.experiences.last7d} />
            <KpiCard label="New (30 days)" value={data.experiences.last30d} />
            <KpiCard
              label="Conversion"
              value={formatPercent(data.conversion.paidRate)}
              hint={`${data.conversion.paidCount} paid / ${data.experiences.total} total`}
              tone={data.conversion.paidRate > 0.3 ? 'good' : 'default'}
            />
          </div>

          <h2 className="mt-8 mb-3 text-sm font-semibold text-slate-700">Revenue</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              label="Last 30 days"
              value={formatCents(data.revenue.last30dCents, data.revenue.currency)}
              tone="good"
            />
            <KpiCard
              label="All time"
              value={formatCents(data.revenue.allTimeCents, data.revenue.currency)}
            />
            <KpiCard
              label="Avg guests / experience"
              value={data.avgGuestsPerExperience.toFixed(1)}
            />
          </div>

          <h2 className="mt-8 mb-3 text-sm font-semibold text-slate-700">Uploads</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Ready" value={data.uploads.ready} tone="good" />
            <KpiCard label="Processing" value={data.uploads.processing} />
            <KpiCard label="Pending" value={data.uploads.pending} />
            <KpiCard
              label="Failed"
              value={data.uploads.failed}
              tone={data.uploads.failed > 0 ? 'warn' : 'default'}
            />
          </div>
        </>
      )}
    </div>
  );
}
